import { updateDoc, doc, deleteDoc, collection, getDocs, query, where } from "firebase/firestore";
import { deleteObject, listAll, ref } from "firebase/storage";
import { getAuth } from "firebase/auth"; // Add auth import

// Add a new function to delete payment documents for a user
export const deletePaymentDocuments = async (userId, db) => {
    if (!userId || !db) {
        console.warn("Missing required parameters for deletePaymentDocuments");
        return false;
    }
    
    try {
        console.log(`Attempting to delete payment documents for user: ${userId}`);
        
        // Delete payment documents in the customers/{userId}/payments collection
        const paymentsCollectionRef = collection(db, `customers/${userId}/payments`);
        const paymentsSnapshot = await getDocs(paymentsCollectionRef);
        
        if (!paymentsSnapshot.empty) {
            console.log(`Found ${paymentsSnapshot.docs.length} payment documents to delete`);
            
            // Delete each payment document - using the exact same implementation as confirmReset
            for (const document of paymentsSnapshot.docs) {
                console.log(`Deleting payment document: ${document.id}`);
                await deleteDoc(doc(db, `customers/${userId}/payments`, document.id));
            }
            
            console.log("All payment documents deleted successfully");
        } else {
            console.log("No payment documents found to delete");
        }
        
        // Also check for and delete checkout_sessions
        const checkoutSessionsRef = collection(db, `customers/${userId}/checkout_sessions`);
        const checkoutSnapshot = await getDocs(checkoutSessionsRef);
        
        if (!checkoutSnapshot.empty) {
            console.log(`Found ${checkoutSnapshot.docs.length} checkout sessions to delete`);
            
            // Delete each checkout session - using the exact same implementation as confirmReset
            for (const document of checkoutSnapshot.docs) {
                console.log(`Deleting checkout session: ${document.id}`);
                await deleteDoc(doc(db, `customers/${userId}/checkout_sessions`, document.id));
            }
            
            console.log("All checkout sessions deleted successfully");
        } else {
            console.log("No checkout sessions found to delete");
        }
        
        // Also check and delete subscriptions if they exist
        try {
            const subscriptionsRef = collection(db, `customers/${userId}/subscriptions`);
            const subscriptionsSnapshot = await getDocs(subscriptionsRef);
            
            if (!subscriptionsSnapshot.empty) {
                console.log(`Found ${subscriptionsSnapshot.docs.length} subscription documents to delete`);
                
                for (const document of subscriptionsSnapshot.docs) {
                    console.log(`Deleting subscription: ${document.id}`);
                    await deleteDoc(doc(db, `customers/${userId}/subscriptions`, document.id));
                }
                
                console.log("All subscription documents deleted successfully");
            }
        } catch (subError) {
            console.warn("Error cleaning up subscriptions:", subError);
            // Non-fatal error, continue with the reset
        }
        
        return true;
    } catch (error) {
        console.error("Error deleting payment documents:", error);
        return false;
    }
};

// Function to update Firestore after soft reset
export const updateFirestoreAfterSoftReset = async (currentUser, db, processedImages, gridSize) => {
    if (!currentUser || !currentUser.uid || !db) {
        console.warn("Missing required parameters for updateFirestoreAfterSoftReset");
        return false;
    }
    
    try {
        const sessionRef = doc(db, 'sessions', currentUser.uid);
        
        // Update the session document with reset state
        // Make sure to explicitly reset all AI-related fields
        await updateDoc(sessionRef, {
            quiltImageUrl: '',
            quiltGrid: [],
            generated: false,
            generateDisabled: false,
            aiGeneratedLayouts: [],
            layouts: [], // Clear legacy layouts field
            layoutSuggestions: [], // IMPORTANT: Clear layout suggestions
            aiLayouts: [], // Clear any other AI layout fields that might exist
            softResetTimestamp: new Date().toISOString(),
            // Add these fields to ensure complete reset of layout-related data
            layout_suggestions: [], // Also clear layout_suggestions (alternate field name)
            selectedLayout: null, // Clear the selected layout
            currentLayoutId: null, // Clear current layout ID if exists
        });
        
        // Also delete payment documents for soft reset - Explicit call with detailed logging
        console.log("Soft reset: Explicitly deleting payment documents");
        const paymentDeletionResult = await deletePaymentDocuments(currentUser.uid, db);
        console.log("Payment document deletion result:", paymentDeletionResult);
        
        // NEW: Clear user_layout documents for this user (but don't delete them)
        try {
            console.log("Clearing user_layout documents for user:", currentUser.uid);
            const userLayoutsRef = collection(db, 'user_layouts');
            const userLayoutQuery = query(userLayoutsRef, where("userId", "==", currentUser.uid));
            const userLayoutSnapshot = await getDocs(userLayoutQuery);
            
            if (!userLayoutSnapshot.empty) {
                console.log(`Found ${userLayoutSnapshot.docs.length} user_layout documents to clear`);
                
                for (const document of userLayoutSnapshot.docs) {
                    console.log(`Clearing user_layout document: ${document.id}`);
                    // Instead of deleting, just mark as reset
                    await updateDoc(doc(db, 'user_layouts', document.id), {
                        resetTimestamp: new Date().toISOString(),
                        isReset: true,
                        active: false
                    });
                }
                
                console.log("All user_layout documents cleared successfully");
            }
        } catch (error) {
            console.error("Error clearing user_layout documents:", error);
        }
        
        // NEW: Clear layout_customization documents for this user (but don't delete them)
        try {
            console.log("Clearing layout_customization documents for user:", currentUser.uid);
            const layoutCustomizationsRef = collection(db, 'layout_customizations');
            const layoutCustomizationQuery = query(layoutCustomizationsRef, where("userId", "==", currentUser.uid));
            const layoutCustomizationSnapshot = await getDocs(layoutCustomizationQuery);
            
            if (!layoutCustomizationSnapshot.empty) {
                console.log(`Found ${layoutCustomizationSnapshot.docs.length} layout_customization documents to clear`);
                
                for (const document of layoutCustomizationSnapshot.docs) {
                    console.log(`Clearing layout_customization document: ${document.id}`);
                    // Instead of deleting, just mark as reset
                    await updateDoc(doc(db, 'layout_customizations', document.id), {
                        resetTimestamp: new Date().toISOString(),
                        isReset: true,
                        active: false
                    });
                }
                
                console.log("All layout_customization documents cleared successfully");
            }
        } catch (error) {
            console.error("Error clearing layout_customization documents:", error);
        }
        
        console.log("Firestore updated after soft reset with processed images preserved and layouts cleared");
        return true;
    } catch (error) {
        console.error("Error updating Firestore after soft reset:", error);
        return false;
    }
};

/**
 * Handles soft reset functionality - keeps images but resets layout
 */
export const confirmSoftReset = (
    uploadedImages,
    gridSize,
    calculateRequiredImages,
    setGenerated,
    setQuiltImageUrl,
    setPreviousLayoutType,
    setGenerateDisabled,
    setIsEditUnlocked,
    setQuiltGrid,
    setFeedbackSubmitted,
    setRating,
    setAIGeneratedLayouts,
    setUploadedImages,
    setGridSize,
    setRequiredImages,
    closeSoftResetModal,
    currentUser,
    db,
    setShowWarning,
    setLayoutSuggestions,
    storage // Add storage parameter for deleting files
) => {
    // Keep the uploaded images, but reset other properties
    const keptImages = uploadedImages.map(img => ({
        ...img,
        processed: true // Ensure all images are marked as processed
    }));

    // Reset the layout-specific states
    setGenerated(false);
    setQuiltImageUrl('');
    setPreviousLayoutType('');
    setGenerateDisabled(false);
    setIsEditUnlocked(false);
    setQuiltGrid([]);
    setFeedbackSubmitted(false);
    setRating(0);
    
    // Clear AI layouts
    setAIGeneratedLayouts([]);
    
    // Clear layout suggestions as well - ensure it's properly handled
    if (setLayoutSuggestions) {
        console.log("Clearing layout suggestions during soft reset");
        setLayoutSuggestions([]);
    } else {
        console.warn("setLayoutSuggestions function not provided to confirmSoftReset");
    }
    
    // Set the modified images
    setUploadedImages(keptImages);

    // Close modal if provided
    if (closeSoftResetModal) {
        closeSoftResetModal();
    }

    // Clear sessionStorage AI-related data - ensure all layout-related fields are removed
    try {
        const firestoreSessionData = sessionStorage.getItem('firestoreSessionData');
        if (firestoreSessionData) {
            const parsedData = JSON.parse(firestoreSessionData);
            // Remove ALL AI/layout-related data but keep other data if needed
            delete parsedData.layoutSuggestions;
            delete parsedData.layout_suggestions; // Also check for alternate field name
            delete parsedData.aiGeneratedLayouts;
            delete parsedData.selectedLayout;
            delete parsedData.currentLayoutId;
            delete parsedData.layouts; // Remove legacy layouts field
            delete parsedData.aiLayouts; // Remove any other AI layout fields
            // Update session storage
            sessionStorage.setItem('firestoreSessionData', JSON.stringify(parsedData));
            console.log("Session storage cleared of all layout-related data");
        }
    } catch (err) {
        console.warn("Error updating sessionStorage during soft reset:", err);
    }

    // Update firestore asynchronously if user is available
    if (currentUser && currentUser.uid && db) {
        // First update general session data
        updateFirestoreAfterSoftReset(currentUser, db, keptImages, gridSize)
            .then(success => {
                console.log("Firestore session updated successfully:", success);
            })
            .catch(err => {
                console.error("Error updating Firestore session:", err);
            });
        
        // Also explicitly delete payment documents as a separate operation
        console.log("Soft reset: Additional explicit payment document deletion");
        deletePaymentDocuments(currentUser.uid, db)
            .then(success => {
                console.log("Additional payment documents deletion result:", success);
            })
            .catch(err => {
                console.error("Error in additional payment document deletion:", err);
            });
        
        // NEW: Delete shared_quilts collection documents
        deleteSharedQuilts(currentUser.uid, db)
            .then(success => {
                console.log("Shared quilts deletion result:", success);
            })
            .catch(err => {
                console.error("Error deleting shared quilts:", err);
            });
        
        // NEW: Delete emboss and user_quilts folders if storage is available
        if (storage) {
            deleteEmbossAndUserQuilts(currentUser.uid, storage)
                .then(success => {
                    console.log("Emboss and user_quilts folders deletion result:", success);
                })
                .catch(err => {
                    console.error("Error deleting emboss and user_quilts folders:", err);
                });
        } else {
            console.warn("Storage not provided, cannot delete emboss and user_quilts folders");
        }
    }

    // Set a message to indicate that the layout has been reset
    if (setShowWarning) {
        setShowWarning('Layout has been reset. Keep your uploaded images and generate a new layout.');
        
        // Clear the warning after 5 seconds
        setTimeout(() => setShowWarning(''), 5000);
    }
};

// Also update the confirmReset function to clear layout suggestions and delete payment documents
export const confirmReset = async (
    sessionId,
    storage,
    db,
    setUploadedImages,
    setGridSize,
    setGenerated,
    setRequiredImages,
    setShowWarning,
    setQuiltImageUrl,
    setPreviousLayoutType,
    setGenerateDisabled,
    setIsEditUnlocked,
    setQuiltGrid,
    setSessionId,
    setFeedbackSubmitted,
    setRating,
    setIsResetLoading,
    setAIGeneratedLayouts,
    closeResetModal,
    setQuiltDimensions,
    setLayoutSuggestions,  // Add this parameter
    setCurrentStep  // Add this parameter
) => {
    // Show loading state
    setIsResetLoading(true);
    
    try {
        console.log("Starting reset process for session:", sessionId);
        
        // First, delete all images from storage
        await deleteAllImagesFromStorage(sessionId, storage);
        
        // Then reset all state variables
        setUploadedImages([]);
        setGridSize('');
        setGenerated(false);
        setRequiredImages(0);
        setQuiltImageUrl('');
        setPreviousLayoutType('');
        setGenerateDisabled(false);
        setIsEditUnlocked(false);
        setQuiltGrid([]);
        setFeedbackSubmitted(false);
        setRating(0);
        setAIGeneratedLayouts([]);
        setLayoutSuggestions([]); // Reset layout suggestions
        
        // Reset quilt dimensions
        setQuiltDimensions({
            quiltWidth: null,
            quiltHeight: null,
            tileWidth: null,
            tileHeight: null,
            columns: 0,
            rows: 0,
            totalTiles: 0,
            tileWidthPixels: 300,
            tileHeightPixels: 300
        });
        
        // Important: Set current step back to 1
        setCurrentStep(1);
        
        // Delete Firestore session data and payment documents
        if (db && sessionId) {
            console.log("Deleting Firestore data for session:", sessionId);
            
            // Delete session document
            await deleteFirestoreSession(sessionId, db);
            
            // Delete payment documents - most important part for fixing the issue
            console.log("Calling deletePaymentDocuments for user:", sessionId);
            const paymentDeleteResult = await deletePaymentDocuments(sessionId, db);
            console.log("Payment documents deletion result:", paymentDeleteResult);
        } else {
            console.warn("Missing db or sessionId, cannot delete Firestore data");
        }
        
        // Reset any form fields
        resetFormFields();
        
        console.log("Reset process completed successfully");
        
        // Show success message
        setShowWarning("Project reset successful. You can now start a new quilt design.");
        
        // After 3 seconds, clear the message
        setTimeout(() => {
            setShowWarning("");
        }, 3000);
        
    } catch (error) {
        console.error("Error during reset:", error);
        setShowWarning("There was an error resetting the project. Please try again.");
    } finally {
        setIsResetLoading(false);
        closeResetModal();
    }
};

export const resetFormFields = () => {
    const inputFields = document.querySelectorAll('input[type="text"], input[type="number"]');
    inputFields.forEach(input => {
        if (input.name && (input.name.includes('quilt') || input.name.includes('tile'))) {
            // Reset the value
            input.value = '';
            
            // Trigger React's change handlers
            const event = new Event('change', { bubbles: true });
            input.dispatchEvent(event);
        }
    });
};

// Add the missing functions for deleting images and sessions
export const deleteAllImagesFromStorage = async (sessionId, storage) => {
    if (!sessionId || !storage) return false;
    
    try {
        console.log("Deleting all images for session:", sessionId);
        
        // Reference to the user's folder in storage
        const storageRef = ref(storage, `quilt_designs/${sessionId}`);
        
        // List all files in the directory
        const listResult = await listAll(storageRef);
        
        // Delete each file
        const deletePromises = listResult.items.map(itemRef => {
            console.log("Deleting file:", itemRef.fullPath);
            return deleteObject(itemRef);
        });
        
        // Wait for all deletions to complete
        await Promise.all(deletePromises);
        
        console.log("All images deleted successfully");
        return true;
    } catch (error) {
        console.error("Error deleting images from storage:", error);
        return false;
    }
};

export const deleteFirestoreSession = async (sessionId, db) => {
    if (!sessionId || !db) return false;
    
    try {
        console.log("Deleting session data from Firestore:", sessionId);
        
        // Delete the main session document
        const sessionRef = doc(db, 'sessions', sessionId);
        await deleteDoc(sessionRef);
        
        console.log("Session data deleted successfully");
        return true;
    } catch (error) {
        console.error("Error deleting session data from Firestore:", error);
        return false;
    }
};

// New function to delete emboss and user_quilts folders during soft reset
export const deleteEmbossAndUserQuilts = async (sessionId, storage) => {
    if (!sessionId || !storage) {
        console.warn("Missing required parameters for deleteEmbossAndUserQuilts");
        return false;
    }

    try {
        // Delete emboss folder inside quilt_designs
        const embossRef = ref(storage, `quilt_designs/${sessionId}/emboss`);
        try {
            const embossListResult = await listAll(embossRef);
            console.log(`Found ${embossListResult.items.length} files in quilt_designs/${sessionId}/emboss`);
            // Delete each file in emboss folder
            await Promise.all(embossListResult.items.map(itemRef => deleteObject(itemRef)));
            console.log(`Deleted all files in quilt_designs/${sessionId}/emboss`);
        } catch (error) {
            // This might fail if folder doesn't exist, which is fine
            console.log(`Note: emboss folder may not exist or couldn't be accessed: ${error.message}`);
        }
        
        // Delete user_quilts folder
        const userQuiltsRef = ref(storage, `user_quilts/${sessionId}`);
        try {
            const userQuiltsListResult = await listAll(userQuiltsRef);
            console.log(`Found ${userQuiltsListResult.items.length} files in user_quilts/${sessionId}`);
            // Delete each file in user_quilts folder
            await Promise.all(userQuiltsListResult.items.map(itemRef => deleteObject(itemRef)));
            console.log(`Deleted all files in user_quilts/${sessionId}`);
        } catch (error) {
            // This might fail if folder doesn't exist, which is fine
            console.log(`Note: user_quilts folder may not exist or couldn't be accessed: ${error.message}`);
        }
        
        return true;
    } catch (error) {
        console.error("Error deleting emboss and user_quilts folders:", error);
        return false;
    }
};

// New function to delete shared_quilts collection documents
export const deleteSharedQuilts = async (userId, db) => {
    if (!userId || !db) {
        console.warn("Missing required parameters for deleteSharedQuilts");
        return false;
    }
    
    try {
        console.log("Deleting shared_quilts documents for user:", userId);
        const sharedQuiltsRef = collection(db, 'shared_quilts');
        const sharedQuiltsQuery = query(sharedQuiltsRef, where("userId", "==", userId));
        const sharedQuiltsSnapshot = await getDocs(sharedQuiltsQuery);
        
        if (!sharedQuiltsSnapshot.empty) {
            console.log(`Found ${sharedQuiltsSnapshot.docs.length} shared_quilts documents to delete`);
            
            for (const document of sharedQuiltsSnapshot.docs) {
                console.log(`Deleting shared_quilts document: ${document.id}`);
                await deleteDoc(doc(db, 'shared_quilts', document.id));
            }
            
            console.log("All shared_quilts documents deleted successfully");
        } else {
            console.log("No shared_quilts documents found to delete");
        }
        
        return true;
    } catch (error) {
        console.error("Error deleting shared_quilts documents:", error);
        return false;
    }
};
